// crewbit-admin/src/lib/scheduleRpc.ts
//
// Canonical wrapper for every Schedule RPC invocation from the admin dashboard.
// Spec: docs/references/schedule/SCHEDULE_MODULE_WIRING_v1.md §2.
//
// Identical contract to crewbit-mobile/lib/scheduleRpc.ts:
// - Hooks call this wrapper exclusively. INV-Schedule-direct-mutation lint (Money's
//   inv-4-direct-mutations.ts extended in Commit B5) enforces no direct
//   supabase.rpc(...) calls outside this file.
// - Wrapper does NOT manipulate args. Hooks own snake_case + p_idempotency_key inclusion.
// - Strict response-shape guard. Anything without success: true|false discriminator
//   is treated as UNKNOWN error and Sentry-captured.
// - alreadyExists flag normalized from the various per-RPC discriminators.
//
// Sentry import differs from mobile (@sentry/react v10 vs @sentry/react-native v7).

import * as Sentry from '@sentry/react';
import { supabase } from './supabase';
import { mapPostgresError, type ScheduleError } from './errorMap';

export type ScheduleRpcResult<T> =
  | { success: true; data: T; alreadyExists: boolean }
  | { success: false; error: ScheduleError };

/**
 * Invoke a Schedule RPC. Returns a discriminated union — never throws on business errors.
 *
 * @param rpcName  the snake_case RPC name as declared in DATA spec §C
 * @param args     RPC arguments matching the function's declared signature exactly
 */
export async function callScheduleRpc<TResult>(
  rpcName: string,
  args: Record<string, unknown>,
): Promise<ScheduleRpcResult<TResult>> {
  const { data, error } = await supabase.rpc(rpcName, args);

  // 1. Postgres-level error. mapPostgresError emits exclusively the infra-code subset
  //    (NETWORK_FAILURE / AUTH_EXPIRED / RLS_DENIED / STALE_PUBLIC_USER_ID / UNKNOWN);
  //    all five are present in ScheduleErrorCode. Cast is safe by construction.
  if (error) {
    return { success: false, error: mapPostgresError(error) as unknown as ScheduleError };
  }

  // 2. Strict response-shape guard.
  if (data == null || typeof data !== 'object' || Array.isArray(data) || !('success' in data)) {
    Sentry.captureMessage(`Malformed RPC response from ${rpcName}`, {
      tags: { rpcName },
      extra: { received: data },
    });
    return {
      success: false,
      error: { code: 'UNKNOWN', message: `RPC ${rpcName} returned unexpected shape` },
    };
  }

  // 3. Discriminated error case.
  if ((data as { success: false; error?: ScheduleError }).success === false) {
    const errPayload = (data as { error?: ScheduleError }).error;
    if (!errPayload || typeof errPayload !== 'object' || !('code' in errPayload)) {
      return {
        success: false,
        error: {
          code: 'UNKNOWN',
          message: `RPC ${rpcName} returned success:false without error payload`,
        },
      };
    }
    return { success: false, error: errPayload };
  }

  // 4. Success case. Normalize already_* discriminators.
  const d = data as Record<string, unknown>;
  const alreadyExists = Boolean(
    d.already_created ||
    d.already_updated ||
    d.already_deleted ||
    d.already_assigned ||
    d.already_at ||
    d.already_rescheduled,
  );

  return {
    success: true,
    data: data as unknown as TResult,
    alreadyExists,
  };
}

// =============================================================================
// callScheduleQueryRpc — read-only STABLE helper variant
// =============================================================================
//
// Schedule query helpers (for example `resolve_schedule_view` and
// `detect_schedule_conflicts`) intentionally do NOT use the
// {success, error?} envelope — they're pure-function helpers consumed inline
// by mutation RPCs and surface UIs (admin pre-flight conflict banner, canvas
// mount zoom/lens default resolution).

export type ScheduleQueryRpcResult<T> =
  | { success: true; data: T }
  | { success: false; error: ScheduleError };

/**
 * Invoke a STABLE read-only Schedule helper RPC. Returns the raw `data` on
 * success; does NOT enforce the {success, error?} envelope contract.
 *
 * Use callScheduleRpc for all mutation-shaped RPCs. Use this only for the
 * declared read-only helpers.
 */
export async function callScheduleQueryRpc<TResult>(
  rpcName: string,
  args: Record<string, unknown>,
): Promise<ScheduleQueryRpcResult<TResult>> {
  const { data, error } = await supabase.rpc(rpcName, args);
  if (error) {
    return { success: false, error: mapPostgresError(error) as unknown as ScheduleError };
  }
  return { success: true, data: data as TResult };
}
